import * as reducer from '@/cascade/cascadeReducer';
import { parse, serialize } from '@/cascade/cascadeSerializer';
import {
  isDraftColumnId,
  ROOT_PARENT,
  type CascadeColumn,
  type CascadeRef,
  type CascadeStack,
} from '@/cascade/types';
import { isColumnDraftState } from '@/composables/useColumnDraftState';
import { getRouter } from '@/const/routerHandle';
import { draftDefinition } from '@/stores/draftDefinitions';
import { isWorkspaceRoute } from '@/stores/workspaceRoute';
import { sidebars, type SidebarType } from '@/registries/sidebars';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

const isKnownInlineType = (type: string): boolean => {
  return sidebars.some((s) => s.type === (type as SidebarType) && !!s.inline);
};

/**
 * Column types that were folded into another and still turn up in old links and
 * bookmarks. TM-9664: a saved plan opens straight on its Verordnung, so the read
 * view it used to open on (same plan id) is gone.
 */
const LEGACY_TYPES: Readonly<Record<string, SidebarType>> = {
  treatmentPlanDetail: 'prescription',
};

const validateStack = (raw: CascadeColumn[]): CascadeColumn[] => {
  // Graceful truncation: drop everything from the first unknown/non-inline type onward.
  const out: CascadeColumn[] = [];
  // Raw index → index in `out`; they part once a merged column is skipped.
  const indexOf: number[] = [];
  for (const parsed of raw) {
    const type = LEGACY_TYPES[parsed.type] ?? parsed.type;
    const parent = parsed.parent === undefined ? undefined : indexOf[parsed.parent];
    const merged = parent !== undefined ? out[parent] : undefined;
    // The old read view and its edit were two columns of the same plan; now one.
    if (merged && merged.type === type && merged.id === parsed.id) {
      indexOf.push(parent!);
      continue;
    }
    if (!isKnownInlineType(type)) break;
    indexOf.push(out.length);
    out.push(parent === undefined ? { ...parsed, type } : { ...parsed, type, parent });
  }
  return out;
};

const readCascadeParam = (param: unknown): string => {
  if (Array.isArray(param)) return param.join('/');
  if (typeof param === 'string') return param;
  return '';
};

export const useCascadeStore = defineStore('cascade', () => {
  const isWorkspace = computed<boolean>(() => isWorkspaceRoute(getRouter().currentRoute.value));

  const stack = computed<CascadeStack>(() => {
    if (!isWorkspace.value) return [];
    return validateStack(parse(readCascadeParam(getRouter().currentRoute.value.params.cascade)));
  });

  function workspaceBasePath(): string {
    // The matched route record's path is like "/office/subscriptions/:cascade(.*)?".
    // Strip the catch-all suffix to recover the workspace base path, and fill in the
    // record's own params (the video call's `/consultation/:callId`). The dashboard's
    // base is the root, so it comes back empty.
    const route = getRouter().currentRoute.value;
    const matched = route.matched[route.matched.length - 1];
    const recordPath = matched?.path ?? route.path;
    return recordPath
      .replace(/\/?:cascade\(.*\)\??$/, '')
      .replace(/:(\w+)(\([^)]*\))?\??/g, (_, key: string) =>
        encodeURIComponent(String(route.params[key] ?? '')),
      );
  }

  function navigate(method: 'push' | 'replace', next: CascadeStack): void {
    if (!isWorkspace.value) return;
    const segment = serialize(next);
    const base = workspaceBasePath();
    const path = segment ? `${base}/${segment}` : base || '/';
    const router = getRouter();
    // ⚠ **The query rides along.** This used to build a bare path, which silently
    // dropped every search parameter on the route — so a workspace that parks a
    // setting in the query (the §302 Stichtag, #1517) lost it the moment a column
    // opened, and the panel came back on a cold link quietly reset to its
    // default. The cascade owns the PATH and has no business editing what sits
    // beside it. Re-serialized here rather than handed to the router as a
    // location object so the pushed value stays the plain URL string the
    // `fullPath` comparison below (and every caller's expectation) is written in.
    //
    // ⚠ **Serialized by the router, never by hand.** The hand-rolled version
    // pushed every value through `URLSearchParams.append(key, value ?? '')`,
    // which writes a valueless key as `key=` — `vue-router` models `?debug` as
    // `debug: null`, so a cascade navigation rewrote it to `?debug=` and broke
    // the very preservation this exists for. `resolve()` is the same serializer
    // that parsed the query in the first place, so the round trip is exact by
    // construction, and its `fullPath` is the plain string the comparison below
    // needs anyway.
    const url = router.resolve({ path, query: router.currentRoute.value.query }).fullPath;
    if (url === router.currentRoute.value.fullPath) return;
    // RestrictedRouter narrows path arguments; the cascade URL is dynamic and
    // not in the type map.
    void (router[method] as (path: string) => Promise<unknown>)(url);
  }

  // Re-trigger attention pulse (ADR-0027 rule D, RS-005). A monotonically
  // increasing signal the strip watches: when the user acts on something already
  // open, nothing opens or closes, but bumping this makes every currently-open
  // column pulse its ring once ("here is everything you have open"). A counter
  // (not a boolean) so back-to-back re-triggers each register as a distinct
  // change even before the previous pulse settles.
  const pulseSignal = ref<number>(0);

  // Narrow (push-nav) page-cover coordination. On narrow viewports the strip is
  // a fixed full-screen overlay; the underlying page (content list + mobile
  // chrome) is hidden ONLY while a column fully covers it. The strip flips this:
  // true once an entering column's transition settles (so the column slides in
  // OVER the still-visible list rather than over a blank page), false the moment
  // the board empties (so the last column slides off REVEALING the list, instead
  // of the list snapping back beside a half-height leaver). LayoutDefault reads
  // it to drive `hideContentForPushNav`. Desktop ignores it (gated on width).
  const pushNavCovered = ref<boolean>(false);

  // Per-column collapse (TM-9455). A minimised column keeps its place in the
  // tree and its panel mounted — only its width drops to a rail, so the space
  // goes to the list pane and its neighbours.
  //
  // Deliberately NOT serialised into the URL: how wide a column is drawn is a
  // view preference, not part of the board's identity, so a shared deep link
  // keeps opening the same columns fully expanded and the URL grammar
  // (cascadeSerializer.ts) stays untouched. The cost is that collapse does not
  // survive a reload — nothing is lost, the column simply comes back full width.
  //
  // Keyed by `type:id`, not by stack index: indices shift whenever a sibling
  // opens or closes, so an index-keyed set would silently hand the collapsed
  // state to an unrelated column.
  const minimizedKeys = ref<ReadonlySet<string>>(new Set());

  const columnKey = (column: CascadeColumn): string => `${column.type}:${column.id}`;

  function isMinimized(column: CascadeColumn): boolean {
    return minimizedKeys.value.has(columnKey(column));
  }

  /**
   * Close the columns that only exist to be looked at BESIDE `index` — today the
   * form preview beside the form editor (`closesWithParent`).
   *
   * Direct children only: a real record opened from this column stands on its own
   * and must survive its parent being put away. Runs before the collapse rather
   * than after, so the row never paints a full-width orphan beside a 52px rail.
   */
  function closeDependents(index: number): void {
    const dependent = stack.value.findIndex(
      (column, i) =>
        i > index &&
        column.parent === index &&
        (!!sidebars.find((entry) => entry.type === column.type)?.inline?.closesWithParent ||
          isPristineCreateColumn(column)),
    );
    if (dependent !== -1) closeColumn(dependent);
  }

  /**
   * A create column opened from another panel that the user never put anything into —
   * "Patient anlegen" opened from the picker in "Programm zuweisen", then abandoned.
   *
   * It goes when its parent is put away, because there is nothing in it to keep: a
   * pristine create column closes without a prompt and leaves no trace anywhere else
   * either (ADR-0029 §A). One that HOLDS something stays, and parks as a draft on its
   * own terms.
   */
  function isPristineCreateColumn(column: CascadeColumn): boolean {
    if (!isDraftColumnId(column.id)) return false;
    if (!draftDefinition(column.type)) return false;
    return !isColumnDraftState(stack.value, column);
  }

  function setMinimized(column: CascadeColumn, minimized: boolean): void {
    if (minimized) {
      const index = stack.value.findIndex((entry) => columnKey(entry) === columnKey(column));
      if (index !== -1) closeDependents(index);
    }
    const next = new Set(minimizedKeys.value);
    if (minimized) next.add(columnKey(column));
    else next.delete(columnKey(column));
    minimizedKeys.value = next;
  }

  // Wide types opened out to 1000px (TM-9664). Every column opens narrow (500px, 375px on a compact page); a type the
  // registry marks `wide` can be widened, and the choice is kept per TYPE, not per column:
  // the width is how the user works with that kind of panel, not a property of one record.
  // So a widened type stays wide for the next record picked from the list, and when it is
  // closed and opened again — in the cascade and in the Entwurf zone alike
  // (DraftZoneColumn reads the same set). Kept in localStorage so it also survives a
  // reload; still not in the URL, so a shared link opens at the receiver's own width.
  const WIDENED_TYPES_STORAGE_KEY = 'cascade_widenedTypes';

  const readWidenedTypes = (): ReadonlySet<string> => {
    try {
      const saved = JSON.parse(localStorage.getItem(WIDENED_TYPES_STORAGE_KEY) ?? '[]');
      return new Set(Array.isArray(saved) ? saved.filter((t) => typeof t === 'string') : []);
    } catch {
      return new Set();
    }
  };

  const widenedTypes = ref<ReadonlySet<string>>(readWidenedTypes());

  /** Drawn narrow: every type, until the user widens a `wide` one. */
  function isNarrowed(column: Pick<CascadeColumn, 'type'>): boolean {
    return !widenedTypes.value.has(column.type);
  }

  function setNarrowed(column: Pick<CascadeColumn, 'type'>, narrowed: boolean): void {
    const next = new Set(widenedTypes.value);
    if (narrowed) next.delete(column.type);
    else next.add(column.type);
    widenedTypes.value = next;
    try {
      localStorage.setItem(WIDENED_TYPES_STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      // Storage unavailable (private mode, quota): the width still holds for the session.
    }
  }

  // Drop minimised keys whose column is no longer open. Without this, collapsing a
  // column, closing it, and opening the same record again later would bring back a
  // collapsed column the user never asked to collapse — the key outlived the thing it
  // described. Runs off `stack`, so it also covers a slot replace and a re-root, not
  // just an explicit ✕.
  watch(stack, (columns) => {
    if (minimizedKeys.value.size === 0) return;
    const live = new Set(columns.map(columnKey));
    const next = new Set([...minimizedKeys.value].filter((key) => live.has(key)));
    if (next.size !== minimizedKeys.value.size) minimizedKeys.value = next;
  });

  // Draft parking on a re-root (TM-9455). ADR-0027 §C-3 clears the whole
  // downstream tree when a root-list row is picked, which used to take an open
  // create column with it. Nothing here has to defend against that any more:
  // `draftStore` keeps its entry when the column leaves the URL, so the draft
  // simply reappears as a parked rail and the user loses nothing. Hence no
  // confirmation on the way through — browsing the list stays uninterrupted, and
  // a draft is still only destroyed by its ✕ or by being saved.

  function openAt(parentIndex: number, column: CascadeColumn, sourceListKey?: string): void {
    // Fan-out tree engine (ADR-0027). The child lands in the `(parentIndex,
    // sourceListKey)` slot; `parentIndex === -1` (ROOT_PARENT) re-roots. Existing
    // workspaces open into the parent's DEFAULT (unnamed) slot — `sourceListKey`
    // undefined — so each parent keeps at most one child and the strip stays a
    // linear spine (degenerate tree). Fan-out verticals (RS-007) pass an explicit
    // per-source-list key so a parent can hold several sibling children at once.
    const result = reducer.open(stack.value, { parentIndex, column, sourceListKey });
    // Re-trigger (rule C-1/D): the {type,id} is already open — no column opens,
    // none closes, the URL is left untouched; instead all open columns pulse.
    if (result.outcome === 'retrigger') {
      // …but a COLLAPSED column unfolds first. To the user a rail reads as closed,
      // so pressing the thing that opens it again has to bring it back — flashing
      // the edge of a 52px strip answers a question they did not ask (TM-9455).
      const existing = stack.value.find((c) => c.type === column.type && c.id === column.id);
      if (existing) setMinimized(existing, false);
      pulseSignal.value++;
      return;
    }
    navigate('push', result.tree);
  }

  // Re-root the board to `root` and dock `child` under it in the
  // `(root, sourceListKey)` slot — a single navigation that replaces the whole
  // existing tree. Backs the `reRootUnder` open intent: a root-list affordance
  // naming a nested record (a treatment-plan / program pill on a patient row)
  // opens `[patientFile, detail]` fresh, rather than the bare detail (openAt with
  // ROOT_PARENT) or the detail hung under an unrelated open patient (openAt with
  // a matched parent). No re-trigger handling: rebuilding an already-open pair
  // yields the same URL, so `navigate` no-ops.
  function openUnderNewRoot(
    root: CascadeColumn,
    child: CascadeColumn,
    sourceListKey?: string,
  ): void {
    const rooted = reducer.reRoot([], root).tree;
    const result = reducer.open(rooted, { parentIndex: 0, column: child, sourceListKey });
    navigate('push', result.tree);
  }

  // Ephemeral hover state (ADR-0027 rule E, RS-006): while a list row is hovered,
  // holds the flat indices of every column that picking that row would close, so
  // the strip can dim exactly them. Empty while nothing is hovered. The trigger
  // is list-row hover (not the column ✕), and the target is the hovered row's
  // `(parent, sourceListKey)` slot subtree — not "the tail to the right".
  const closePreviewIndices = ref<readonly number[]>([]);

  // Preview what opening `column` from the `(parentIndex, sourceListKey)` slot
  // would CLOSE — marking only columns that close, never ones merely replaced.
  // Picking a row swaps that slot's occupant in place (same column position, new
  // record): the occupant does not close, it transforms. What actually closes is
  // everything drilled in BENEATH the occupant — the outgoing record's subtree —
  // so the ✕ marks exactly those descendants. Nothing when it is a re-trigger
  // (rule D), the slot is empty (→ append), or the occupant is a leaf (a pure
  // swap, no collateral). This holds uniformly for a re-root too: hovering a
  // page-list row would replace the root column, so only its descendants dim —
  // an already-shallow board (just the root open) stays silent while skimming.
  function previewSlotOpen(parentIndex: number, column: CascadeRef, sourceListKey?: string): void {
    if (reducer.isOpen(stack.value, column)) {
      closePreviewIndices.value = [];
      return;
    }
    const childIndex = reducer.slotChildIndex(stack.value, parentIndex, sourceListKey);
    closePreviewIndices.value =
      childIndex === null ? [] : reducer.descendantIndices(stack.value, childIndex);
  }

  // Preview closing the column at `index` via its header ✕ — marking only the
  // COLLATERAL, never the column the user is acting on. The ✕ does remove that
  // column too (`closeSubtree` / `closeColumn`), but the hovered ✕ already says
  // so at the point of the cursor; dimming its own column on top of that reads
  // as noise and hides the one thing worth warning about — the descendants that
  // vanish silently along with it. So this is `descendantIndices`, same as the
  // list-row path, and a leaf column's ✕ previews nothing at all: there is no
  // collateral to warn about.
  function previewCloseColumn(index: number): void {
    closePreviewIndices.value = reducer.descendantIndices(stack.value, index);
  }

  function clearClosePreview(): void {
    closePreviewIndices.value = [];
  }

  // Close a column via its header ✕: removes it AND its subtree (siblings and
  // ancestors untouched); closing the root clears the board (ADR-0027 §C).
  function closeColumn(index: number): void {
    navigate('push', reducer.closeSubtree(stack.value, index));
  }

  function clear(): void {
    navigate('push', []);
  }

  function swapDraftId(realId: string, type?: SidebarType, draftId?: string): void {
    navigate('replace', reducer.swapDraftId(stack.value, realId, type, draftId));
  }

  /**
   * `next` takes the place of the open column `current` beside the same parent, in one
   * navigation; `current` leaves with its subtree. For a saved create column whose work
   * goes on elsewhere (a Verordnung saved to plan its appointments, #1939): a close followed
   * by an open would race, the open building on the stack the close has not replaced
   * yet. Replaces the history entry, so Back does not return to the finished create
   * column. False when `current` is not open, or `next` is already open elsewhere.
   */
  function replaceColumn(current: CascadeRef, next: CascadeColumn): boolean {
    const index = stack.value.findIndex((c) => c.type === current.type && c.id === current.id);
    if (index === -1) return false;
    const { parent, sourceListKey } = stack.value[index];
    const result = reducer.open(stack.value, {
      parentIndex: parent ?? ROOT_PARENT,
      column: next,
      sourceListKey,
    });
    if (result.outcome === 'retrigger') return false;
    navigate('replace', result.tree);
    return true;
  }

  // Apply a tab selection to `base[index]`, returning the resulting stack (or
  // null when the index is absent). Pure: operates on the passed stack so it can
  // be composed with a prior truncation in a single navigation.
  function applyColumnTab(base: CascadeStack, index: number, tab: string): CascadeColumn[] | null {
    const current = base[index];
    if (!current) return null;
    // Default-tab omission (RS-005): when the chosen tab is the registry's
    // declared default for this type, drop the `tab` field so the URL stays
    // `type:id` rather than `type:id@<default>`. Reads stay coherent because
    // `desiredTabView` in CascadeColumn falls back to the registry default.
    const entry = sidebars.find((s) => s.type === (current.type as SidebarType));
    const defaultTab = entry?.inline?.defaultTabView;
    const nextTab = defaultTab !== undefined && tab === defaultTab ? undefined : tab;
    return base.map((c, i) => {
      if (i !== index) return c;
      // Preserve all existing fields (notably `parent` and `sourceListKey`):
      // dropping them would serialise this segment without its `^parent` tag, so
      // `parseTree` truncates the tree here — a nested tab switch would remove
      // the column and everything after it instead of merely changing its tab.
      const updated: CascadeColumn = { ...c };
      if (nextTab !== undefined) updated.tab = nextTab;
      else delete updated.tab;
      return updated;
    });
  }

  function setColumnTab(index: number, tab: string): void {
    const current = stack.value[index];
    if (!current) return;
    const next = applyColumnTab(stack.value, index, tab);
    if (!next || current.tab === next[index].tab) return;
    // `push`, not `replace` (TM-9521). A tab flip IS a step the user took, and
    // the browser back button has to undo the steps in the order they were made:
    // with `replace` a column's whole tab history collapsed into the single entry
    // that opened it, so one back press from the fourth tab closed the entire
    // panel instead of walking back through the three tabs before it. Pushing
    // makes back retrace the tabs first and close the column only once its own
    // history is spent — the same "back undoes the last thing I did" the overlay
    // renderer already gives through `useHistoryIntercept`'s tab layers.
    //
    // Safe to push because `setColumnTab` only ever runs from a real user gesture
    // (a tab click or swipe writing `CascadeColumn`'s `activeTabView`, which
    // no-ops when the view is unchanged) — nothing flips tabs programmatically on
    // mount, so no column open can spray entries into the history.
    navigate('push', next);
  }

  return {
    isWorkspace,
    stack,
    openAt,
    openUnderNewRoot,
    pulseSignal,
    pushNavCovered,
    isMinimized,
    setMinimized,
    isNarrowed,
    setNarrowed,
    closeColumn,
    closePreviewIndices,
    previewSlotOpen,
    previewCloseColumn,
    clearClosePreview,
    clear,
    swapDraftId,
    replaceColumn,
    setColumnTab,
  };
});
